import Cookies from "universal-cookie";
const cookies = new Cookies();

export const AUTH_COOKIE_MAX_AGE_SEC = 10 * 60 * 60;

function cookieDomain() {
  const domain = process.env.REACT_APP_COOKIE_DOMAIN;
  return domain ? domain : undefined;
}

export function isRealAuthToken(token: unknown): token is string {
  return (
    typeof token === "string" &&
    token.length > 0 &&
    token !== "undefined" &&
    token !== "null"
  );
}

export const setAuthCookie = (token: string) => {
  if (!isRealAuthToken(token)) return;
  const domain = cookieDomain();
  cookies.set("skyTokenFront", token, {
    path: "/",
    maxAge: AUTH_COOKIE_MAX_AGE_SEC,
    ...(domain ? { domain } : {}),
  });
};

export const Logout = (e: any = null) => {
  if (e) e.preventDefault();
  const domain = cookieDomain();
  const expire = "Thu, 01 Jan 1970 00:00:01 GMT";
  document.cookie = `skyTokenFront=; Path=/; Expires=${expire}; Max-Age=0`;
  document.cookie = `skyTokenFront=; Path=/; Expires=${expire}; Max-Age=0; SameSite=Lax`;
  if (domain) {
    document.cookie = `skyTokenFront=; Path=/; Domain=${domain}; Expires=${expire}; Max-Age=0`;
    cookies.remove("skyTokenFront", { domain, path: "/" });
  }
  cookies.remove("skyTokenFront", { path: "/" });
  cookies.remove("skyTokenFront");
  localStorage.clear();
  sessionStorage.clear();
  window.location.reload();
};

export const getImageUrl = (path?: string | null) => {
  if (!path) return "";
  const value = String(path);
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  return `${process.env.REACT_APP_BASE_POINT || ""}${value}`;
};

const FAVICON_CACHE_KEY = "site-favicon";

const isUploadedIcon = (href: string) => /^https?:\/\//i.test(href);

const rememberFavicon = (href: string) => {
  try {
    localStorage.setItem(
      FAVICON_CACHE_KEY,
      JSON.stringify({ host: window.location.hostname, href }),
    );
  } catch {
    return;
  }
};

const readCachedFavicon = () => {
  try {
    const data = JSON.parse(localStorage.getItem(FAVICON_CACHE_KEY) || "");
    if (!data || data.host !== window.location.hostname) return "";
    const href = String(data.href || "");
    return isUploadedIcon(href) ? href : "";
  } catch {
    return "";
  }
};

const upsertIconLink = (id: string, rel: string, href: string) => {
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = id;
    document.head.appendChild(link);
  }
  link.rel = rel;
  link.removeAttribute("sizes");
  link.removeAttribute("type");
  if (link.getAttribute("href") !== href) {
    link.setAttribute("href", href);
  }
};

/** Use the uploaded file unchanged. No placeholder artwork and no resize. */
export const applySiteFavicon = (domainDetails?: any) => {
  if (typeof document === "undefined") return;
  const uploaded =
    domainDetails?.favicon && String(domainDetails.favicon).trim()
      ? getImageUrl(String(domainDetails.favicon).trim())
      : "";
  const href = isUploadedIcon(uploaded) ? uploaded : readCachedFavicon();
  if (!href) return;
  rememberFavicon(href);
  upsertIconLink("site-favicon", "icon", href);
  upsertIconLink("site-apple-touch-icon", "apple-touch-icon", href);
};

export const getBetTable = (TABLE: string) => {
  switch (TABLE) {
    case "odds":
      return "t1";
    case "bookMark":
      return "t2";
    case "session":
      return "t3";
    case "premium":
      return "t4";
    default:
      break;
  }
};

export function descendingComparator(
  a: { [x: string]: number },
  b: { [x: string]: number },
  orderBy: string | number,
) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator(order: string, orderBy: any) {
  return order === "desc"
    ? (a: any, b: any) => descendingComparator(a, b, orderBy)
    : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

export function stableSort(
  array: any[],
  comparator: (arg0: any, arg1: any) => any,
) {
  const stabilizedThis = array.map((el: any, index: any) => [el, index]);
  stabilizedThis.sort((a: number[], b: number[]) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el: any[]) => el[0]);
}

export function getContactValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  const text = String(value || "").trim();
  return text ? [text] : [];
}

export function isContactShowing(site: any, key: string): boolean {
  return site?.contactShowing?.[key] !== false;
}

export function isSignupShowing(site: any): boolean {
  const value = site?.contactShowing?.signup;
  return value === true || value === "true" || value === 1;
}

export function getSignupHref(site: any): string {
  const raw = String(site?.signup || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/") && !raw.startsWith("//")) return raw;
  if (/^[\w.-]+\.[a-z]{2,}([/:?#].*)?$/i.test(raw)) return `https://${raw}`;
  return "";
}

export function getVisibleContacts(site: any, key: string): string[] {
  if (!isContactShowing(site, key)) return [];
  return getContactValues(site?.[key]);
}

export function getContactChannel(site: any, key: string) {
  return {
    showing: isContactShowing(site, key),
    values: getContactValues(site?.[key]),
  };
}
